/* eslint-disable indent */
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AnalyticsType,
  sendAnalyticEvent,
  sendUserIdToAnalytics,
} from '@src/components/web-analytic';
import {
  ClientCategory,
  Product,
  adriverEvents,
  analyticEvents,
  salesNinjaEvents,
} from '@src/constants';
import { AuthActionTypes, Store } from '@src/store';
import type { RegistrationResponse } from '@src/types';
import {
  getAdditionalRiskCodesToString,
  getMainRiskCodesToString,
  handleAnalyticsEvents,
  subObjectConverter,
} from '@src/utils';

import { useCustomerEventRegister } from './use-customer-event-register';
import { useRequest } from './use-request';

// Hужен для того, что бы в яндекс метрику не попали настоящие данные
const SECRET_COEFFICIENT = 1.3;

export function useRegistrationRequest<T>(deps: T[] = []) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    state: {
      stateAuth: { authTokens },
      stateUser: {
        userRegistrationData,
        preset,
        selectedProduct,
        clientCategoryFromUrl,
        profile,
        cid,
        userPolicies,
      },
      stateFormIFLFlat: {
        risks: flatRisks,
        insuranceSubproducts: flatInsuranceSubproducts,
        getPricesFull: flatGetPricesFull,
        selectedDuration: flatSelectedDuration,
      },
      stateFormIFLHouse: {
        risks: houseRisks,
        insuranceSubproducts: houseInsuranceSubproducts,
        getPricesFull: houseGetPricesFull,
        selectedDuration: houseSelectedDuration,
      },
    },
    dispatch,
  } = useContext(Store);
  const flatSubscriptionPrice = flatGetPricesFull?.prices.find(
    ({ duration }) => duration === flatSelectedDuration
  )?.premiumAndDelta;
  const houseSubscriptionPrice = houseGetPricesFull?.prices.find(
    ({ duration }) => duration === houseSelectedDuration
  )?.premiumAndDelta;
  const isNeedToRegisterAsVip =
    selectedProduct === Product.HOUSE &&
    clientCategoryFromUrl === ClientCategory.VIP &&
    preset;

  const {
    isLoading: isRegistrationLoading,
    error: registrationError,
    res: registrationRes,
    refetch: registrationRefetch,
  } = useRequest<RegistrationResponse>(
    'registrationRequest',
    'post',
    '/v1/user/registration',
    {
      user: {
        ...userRegistrationData,
        ...(isNeedToRegisterAsVip && { clientCategory: ClientCategory.VIP }),
        userPolicies,
      },
    },
    deps,
    true,
    authTokens?.authorization?.accessToken
  );

  useEffect(() => {
    if (registrationRes && cid && profile?.profile.userId) {
      const includedProducts = [
        Product.PETS,
        Product.APARTMENT,
        Product.SPORT,
        Product.NS,
      ];

      if (includedProducts.includes(selectedProduct as Product)) {
        sendAnalyticEvent(
          analyticEvents.cpaScaleoConvStatus,
          { cid, adv_order_id: profile?.profile.userId },
          AnalyticsType.cpaScaleo
        );
      }
    }
  }, [registrationRes]);

  useEffect(() => {
    if (
      registrationError &&
      registrationError?.code !== 'ERR_NETWORK' &&
      registrationError?.response?.data?.code !== 'TECHNICAL_ERROR'
    ) {
      const e = registrationError?.response?.status;

      if (e === 401) {
        dispatch({
          type: AuthActionTypes.SetAuthorizeFailState,
          payload: {
            title: t('COMMON:errors.somethingWentWrong'),
            subtitle: t('COMMON:errors.retryRegistration'),
            refRoute: '/login',
          },
        });
      } else {
        dispatch({
          type: AuthActionTypes.SetAuthorizeFailState,
          payload: {
            title: t('COMMON:errors.sendingError'),
            subtitle: t('COMMON:errors.retryRegistrationOrWriteToSupport'),
            refRoute: '/login',
          },
        });
      }

      navigate('/authorize-fail');
    }
  }, [registrationError]);

  const flatContextData = {
    risks: getMainRiskCodesToString(flatRisks),
    additionalRisks: getAdditionalRiskCodesToString(flatRisks),
    subObjects:
      flatInsuranceSubproducts?.subObjects &&
      JSON.stringify(subObjectConverter(flatInsuranceSubproducts.subObjects)),
  };

  const houseContextData = {
    risks: getMainRiskCodesToString(houseRisks),
    additionalRisks: getAdditionalRiskCodesToString(houseRisks),
    subObjects:
      houseInsuranceSubproducts?.subObjects &&
      JSON.stringify(subObjectConverter(houseInsuranceSubproducts.subObjects)),
  };

  const { refetch: refetchCustomerEventRegister } = useCustomerEventRegister({
    eventCode: analyticEvents.authRegistrationCompleted,
    contextData: {
      birthDate: profile?.profile.birthDate,
      firstName: profile?.profile.firstName,
      lastName: profile?.profile.lastName,
      middleName: profile?.profile.middleName,
      ...(selectedProduct === Product.APARTMENT ? flatContextData : {}),
      ...(selectedProduct === Product.HOUSE ? houseContextData : {}),
    },
  });

  useEffect(() => {
    if (!isRegistrationLoading && registrationRes) {
      if (registrationRes?.authorization?.csrfToken) {
        localStorage.setItem(
          'csrfToken',
          registrationRes?.authorization?.csrfToken
        );
      }
      dispatch({
        type: AuthActionTypes.SetAuthTokens,
        payload: registrationRes,
      });

      const userId = profile?.profile?.userId;

      if (userId) {
        sendUserIdToAnalytics(userId);
        localStorage.setItem('userId', userId);
      }

      let myTrackerLogin = analyticEvents.completeLogin;

      switch (selectedProduct) {
        case Product.SPORT:
          myTrackerLogin = analyticEvents.sportCompleteLogin;
          sendAnalyticEvent(
            analyticEvents.sportSignupCompleted,
            undefined,
            AnalyticsType.adriver
          );
          sendAnalyticEvent(
            salesNinjaEvents.authSportSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authSportRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
        case Product.APARTMENT:
          myTrackerLogin = analyticEvents.flatCompleteLogin;
          sendAnalyticEvent(
            analyticEvents.flatSignupCompleted,
            undefined,
            AnalyticsType.adriver
          );
          sendAnalyticEvent(
            salesNinjaEvents.authFlatSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authFlatRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
        case Product.HOUSE:
          sendAnalyticEvent(
            salesNinjaEvents.authHouseSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authHouseRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
        case Product.NS:
          sendAnalyticEvent(
            salesNinjaEvents.authNSSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authNSRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
        case Product.MITE:
          myTrackerLogin = analyticEvents.antimiteCompleteLogin;
          sendAnalyticEvent(
            analyticEvents.miteSignupCompleted,
            undefined,
            AnalyticsType.adriver
          );
          sendAnalyticEvent(
            salesNinjaEvents.authMiteSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authMiteRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
        case Product.PETS:
          myTrackerLogin = analyticEvents.petCompleteLogin;
          sendAnalyticEvent(
            analyticEvents.petsSignupCompleted,
            undefined,
            AnalyticsType.adriver
          );
          sendAnalyticEvent(
            salesNinjaEvents.authPetSignupCompleted,
            null,
            AnalyticsType.salesNinja
          );
          sendAnalyticEvent(
            adriverEvents.authPetRegistrationCompleted,
            null,
            AnalyticsType.adriver
          );
          break;
      }

      sendAnalyticEvent(myTrackerLogin, {}, AnalyticsType.myTracker);
      sendAnalyticEvent(selectedProduct as string, {}, AnalyticsType.roxot);

      if (!!selectedProduct) {
        refetchCustomerEventRegister();
        handleAnalyticsEvents.handleRegistrationComplete(
          selectedProduct,
          !!preset,
          selectedProduct === Product.APARTMENT ||
            selectedProduct === Product.HOUSE
            ? {
                price:
                  Number(flatSubscriptionPrice || houseSubscriptionPrice) *
                  SECRET_COEFFICIENT,
              }
            : undefined
        );
      }

      if (preset) {
        sendAnalyticEvent(analyticEvents.signupPresetCompleted);
      } else {
        if (registrationRes?.isVip) {
          localStorage.setItem('clientCategory', ClientCategory.VIP);
          sendAnalyticEvent(analyticEvents.signupCompleted, {
            registration: 'y',
            VIP: 'y',
          });
        } else {
          localStorage.setItem('clientCategory', ClientCategory.STANDART);
          sendAnalyticEvent(analyticEvents.signupCompleted, {
            registration: 'y',
          });
        }
      }
    }
  }, [isRegistrationLoading, registrationRes]);

  return {
    isRegistrationLoading,
    registrationError,
    registrationRes,
    registrationRefetch,
  };
}
